import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import WaveLines from '@/components/generals/wave-lines'
import { getCategoryStyle } from '@/components/shop/category-style'

interface ProductImageProps {
    src: string | null
    alt: string
    categorySlug: string | null
    className?: string
    iconClassName?: string
    loading?: 'lazy' | 'eager'
}

/*
 * Shows the product photo, or a tinted tile with the category icon until a
 * photo is added in the CMS.
 */
const ProductImage: FC<ProductImageProps> = ({
    src,
    alt,
    categorySlug,
    className,
    iconClassName,
    loading = 'lazy'
}): ReactNode => {
    const [failed, setFailed] = useState(false)

    if (src && !failed) {
        return (
            <img
                src={src}
                alt={alt}
                loading={loading}
                decoding='async'
                onError={() => setFailed(true)}
                className={cn('size-full object-cover', className)}
            />
        )
    }

    const { icon: Icon, tint } = getCategoryStyle(categorySlug)

    return (
        <div
            aria-hidden='true'
            className={cn('relative flex size-full items-center justify-center overflow-hidden text-primary', tint, className)}
        >
            <WaveLines className='absolute inset-x-0 bottom-0 h-1/2 w-full text-primary/10' lines={5} />
            <Icon className={cn('relative size-12 opacity-70', iconClassName)} strokeWidth={1.25} />
        </div>
    )
}

export default ProductImage
